// sections/SectionMainInfo.jsx

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  SvgIcon,
} from "@mui/material";
import MDEditor from "components/MDEditor";
import OccasionsModal from "modals/OccasionsModal"; // adjust to your real path
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LanguageIcon from "@mui/icons-material/Language";
import axios from "axios";
import config from "../../../config";

function TikTokIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.53V6.77a4.85 4.85 0 01-1.01-.08z"/>
    </SvgIcon>
  );
}


function ensureArray(val) {
  return Array.isArray(val) ? val : [];
}


const whiteField = {
  InputLabelProps: { sx: { color: "#fff" } },
  FormHelperTextProps: { sx: { color: "rgba(255,255,255,0.75)" } },
  sx: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
      "&.Mui-focused fieldset": { borderColor: "#fff" },
    },
    "& .MuiSvgIcon-root": { color: "#fff" },
  },
};

function SectionCard({ title, subtitle, right, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "#fff" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>

          {right}
        </Stack>

        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.3)" }} />

        {children}
      </CardContent>
    </Card>
  );
}

export default function SectionMainInfo({
  form,
  setField,
  setNested,
  occasionOptions,
  setOccasionOptions,
  notify,
  dirty,
  onSaved,
}) {
  const { t, i18n } = useTranslation();
  const [occOpen, setOccOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);



  const save = async () => {
    const payload = {
      name: form.name,
      occasions: ensureArray(form.occasions),
      info: form.info,
      contactTimes: form.contactTimes || "All day",
      contact_info: {
        phone: form.contact_info?.phone || "",
        email: form.contact_info?.email || "",
        website: form.contact_info?.website || "",
        facebook: form.contact_info?.facebook || "",
        instagram: form.contact_info?.instagram || "",
        tiktok: form.contact_info?.tiktok || "",
        youtube: form.contact_info?.youtube || "",
      },
    };
    try {
      setSaving(true);

      await axios.put(`${config.BACKEND_BASE_URL}/api/admin/info/update/main-info`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify?.("success", t("info.updateSuccess"));
      onSaved?.();
    } catch (err) {
      console.error("MainInfo update failed:", err);
      notify?.("error", t("info.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  // form.occasions is { en, gr }[] — convert to string[] for modal toggle logic
  const selectedOccasionValues = useMemo(
    () => ensureArray(form.occasions).map((o) => (typeof o === "string" ? o : o.en)),
    [form.occasions]
  );

  const selectedOccasions = ensureArray(form.occasions);

  return (
    <SectionCard
      title={t("info.main.sectionTitle")}
      subtitle={t("info.main.subtitle")}
      right={
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? t("common.saving") : t("info.main.save")}
        </Button>
      }
    >
      <Stack spacing={2}>
        {/* Category (display only) */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                {t("info.main.parentCategory")}
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ color: "#fff" }}>
                {form.parentCategory ? t(`register.parentCategories.${form.parentCategory}`, form.parentCategory) : "N/A"}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                {t("info.main.category")}
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ color: "#fff" }}>
                {form.category ? t(`categories.${form.category}`, form.category) : "N/A"}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        {/* Name */}
        <TextField
          label={t("info.main.businessName")}
          value={form.name || ""}
          onChange={(e) => setField("name", e.target.value)}
          fullWidth
          {...whiteField}
        />

        {/* Occasions trigger box */}
        <Box
          onClick={() => setOccOpen(true)}
          role="button"
          tabIndex={0}
          sx={{
            border: "1px solid rgba(255,255,255,0.35)",
            borderRadius: 2,
            p: 2,
            cursor: "pointer",
            bgcolor: "rgba(255,255,255,0.06)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={900} sx={{ color: "#fff" }}>
              {t("info.main.occasions")}
            </Typography>

            <Box
              sx={{
                minWidth: 34,
                height: 26,
                px: 1,
                borderRadius: 999,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255,255,255,0.22)",
                border: "1px solid rgba(255,255,255,0.45)",
                color: "#fff",
                fontWeight: 900,
                fontSize: 13,
              }}
            >
              {selectedOccasions.length}
            </Box>
          </Stack>

          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: "6px 8px" }}>
            {selectedOccasions.length === 0 ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.80)" }}>
                {t("info.main.clickToSelect")}
              </Typography>
            ) : (
              selectedOccasions.slice(0, 8).map((occ, idx) => {
                const label = typeof occ === "string"
                  ? occ
                  : (i18n.language === "gr" ? (occ.gr || occ.en) : (occ.en || occ.gr));
                return (
                  <Chip
                    key={idx}
                    label={label}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.28)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.65)",
                      fontWeight: 700,
                      "& .MuiChip-label": { color: "#fff" },
                    }}
                  />
                );
              })
            )}

            {selectedOccasions.length > 8 ? (
              <Chip
                size="small"
                label={`+${selectedOccasions.length - 8}`}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.14)",
                  border: "1px dashed rgba(255,255,255,0.55)",
                  fontWeight: 800,
                }}
              />
            ) : null}
          </Box>
        </Box>

        {/* Description */}
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={900}
            sx={{ color: "#fff", mb: 1 }}
          >
            {t("info.main.description")}
          </Typography>

          <MDEditor
            value={form.info || ""}
            onChange={(value) => setField("info", value)}
            sx={{
              "& .ql-toolbar": {
                backgroundColor: "rgba(255,255,255,0.08)",
                borderColor: "rgba(255,255,255,0.25)",
              },
              "& .ql-container": {
                backgroundColor: "rgba(255,255,255,0.05)",
                color: "#fff",
                borderColor: "rgba(255,255,255,0.25)",
              },
              "& .ql-editor": {
                color: "#fff",
                minHeight: "180px",
              },
              "& .ql-stroke": { stroke: "#fff" },
              "& .ql-fill": { fill: "#fff" },
              "& .ql-picker": { color: "#fff" },
            }}
          />
        </Box>


        {/* Contact times */}
        <TextField
          label={t("info.main.contactTimes")}
          value={form.contactTimes || ""}
          onChange={(e) => setField("contactTimes", e.target.value)}
          fullWidth
          {...whiteField}
        />

        <Divider sx={{ borderColor: "rgba(255,255,255,0.3)" }} />

        <Typography fontWeight={900} sx={{ color: "#fff" }}>
          {t("info.main.publicContact")}
        </Typography>

        <Grid container >
          <Grid item xs={12} mt={1}>
            <TextField
              label={t("info.main.phone")}
              value={form.contact_info?.phone || ""}
              onChange={(e) => setNested("contact_info", "phone", e.target.value)}
              fullWidth
              {...whiteField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                  </InputAdornment>
                ),
              }}
            />

          </Grid>

          <Grid item xs={12} mt={2}>
            <TextField
              label="Email"
              value={form.contact_info?.email || ""}
              onChange={(e) => setNested("contact_info", "email", e.target.value)}
              fullWidth
              {...whiteField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                  </InputAdornment>
                ),
              }}
            />

          </Grid>

          <Grid item xs={12} mt={2}>
            <TextField
              label="Website"
              value={form.contact_info?.website || ""}
              onChange={(e) => setNested("contact_info", "website", e.target.value)}
              fullWidth
              {...whiteField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={12} mt={2}>
            <TextField
              label={t("info.main.facebookLink")}
              value={form.contact_info?.facebook || ""}
              onChange={(e) => setNested("contact_info", "facebook", e.target.value)}
              fullWidth
              {...whiteField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FacebookIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                  </InputAdornment>
                ),
              }}
            />

          </Grid>

          <Grid item xs={12} md={12} mt={2}>
            <TextField
              label={t("info.main.instagramLink")}
              value={form.contact_info?.instagram || ""}
              onChange={(e) => setNested("contact_info", "instagram", e.target.value)}
              fullWidth
              {...whiteField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InstagramIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={12} mt={2}>
            <TextField
              label="TikTok"
              value={form.contact_info?.tiktok || ""}
              onChange={(e) => setNested("contact_info", "tiktok", e.target.value)}
              fullWidth
              {...whiteField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <TikTokIcon sx={{ color: "rgba(255,255,255,0.85)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={12} mt={2}>
            <TextField
              label="YouTube"
              value={form.contact_info?.youtube || ""}
              onChange={(e) => setNested("contact_info", "youtube", e.target.value)}
              fullWidth
              {...whiteField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <YouTubeIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

        </Grid>
      </Stack>

      <OccasionsModal
        open={occOpen}
        onClose={() => setOccOpen(false)}
        options={occasionOptions}
        lang={i18n.language}
        selected={selectedOccasionValues}
        setSelected={(values) => {
          const result = values.map((v) => {
            const opt = (occasionOptions || []).find((o) => o.value === v);
            return opt ? { en: opt.nameEn, gr: opt.nameGr || "" } : { en: v, gr: "" };
          });
          setField("occasions", result);
        }}
        addCustomOption={({ nameEn, nameGr }) => {
          setOccasionOptions((prev) => [...prev, { value: nameEn, nameEn, nameGr }]);
          setField("occasions", [...ensureArray(form.occasions), { en: nameEn, gr: nameGr }]);
        }}
      />
    </SectionCard>
  );
}
